// MaturaCentralFromInscribedClosedTask.js
// Zadanie maturalne zamknięte: kąt wpisany i kąt AOB

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import CircleAnglesDiagram from "../../../../components/diagrams/CircleAnglesDiagram";
import MaturaCircleTask21Diagram from "../../../../components/diagrams/MaturaCircleTask21Diagram";

const LETTERS = ["A", "B", "C", "D"];

function shuffle(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// pomocniczo: zbieramy 4 odpowiedzi (poprawna, 2x mniejszy, 2x większy, pośredni)
function buildAngles(alpha_w, alpha_sr) {
  const raw = [
    alpha_sr, // poprawna
    Math.round(alpha_sr / 2), // 2x mniejszy
    2 * alpha_sr, // 2x większy
    alpha_sr + 10, // pośredni (potem ewentualnie poprawimy)
    alpha_sr - 10,
    alpha_sr + 20,
    alpha_sr - 20,
  ];

  const set = new Set();

  // bierzemy tylko dodatnie kąty
  raw.forEach((x) => {
    if (x > 0) set.add(x);
  });

  // jeśli z jakiegoś powodu wypadnie mniej niż 4 (duplikaty itp.),
  // dorzucamy jeszcze kilka sensownych kątów „w okolicy”
  const deltas = [10, 20, 30, 40];
  let i = 0;
  while (set.size < 4 && i < deltas.length) {
    const delta = deltas[i];
    const sign = Math.random() < 0.5 ? -1 : 1;
    const candidate = alpha_sr + sign * delta;
    if (candidate > 0 && !set.has(candidate)) {
      set.add(candidate);
    }
    i++;
  }

  // ostatecznie bierzemy dokładnie 4 różne wartości
  return Array.from(set).slice(0, 4);
}

function createOriginalTask() {
  const angles = [20, 40, 50, 80];
  const options = angles.map((angle, index) => ({
    angle,
    value: angle,
    latex: `${angle}^{\\circ}`,
    label: LETTERS[index],
    isCorrect: index === 3,
  }));

  return {
    type: "MaturaCentralFromInscribedClosedTask",
    data: {
      alpha_w: 40,
      alpha_sr: 80,
      options,
      correctIndex: 3,
      originalSharedDiagram: true,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: "21.1",
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") {
    return createOriginalTask();
  }

  // kąt wpisany – ładne wartości, żeby nie był zbyt mały
  // 45° i 90° są tu zbyt szczególne (pojawia się symetria / półokrąg),
  // więc w wariantach ćwiczeniowych zostawiamy zwykłe kąty maturalne.
  const nice = [30, 36, 40, 50, 54, 60, 72];
  const alpha_w = nice[Math.floor(Math.random() * nice.length)]; // kąt wpisany
  const alpha_sr = 2 * alpha_w; // szukany kąt AOB

  // 4 kąty do odpowiedzi (zawsze: poprawny, 2x mniejszy, 2x większy, pośredni)
  const pickedAngles = buildAngles(alpha_w, alpha_sr);

  const rawOptions = pickedAngles.map((deg) => ({
    angle: deg,
    isCorrect: deg === alpha_sr,
  }));

  const shuffled = shuffle(rawOptions);

  const options = shuffled.map((opt, idx) => ({
    ...opt,
    label: LETTERS[idx],
    value: opt.angle,
    latex: `${opt.angle}^{\\circ}`,
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaCentralFromInscribedClosedTask",
    data: {
      alpha_w,
      alpha_sr,
      options,
      correctIndex,
    },
  };
}

export function renderTask({ data }) {
  const { alpha_w, options } = data;
  const centralDeg = 2 * alpha_w;

  if (data.originalSharedDiagram) {
    return (
      <div className="space-y-3" style={{ textAlign: "left" }}>
        <p>
          Punkty <InlineMath math={"A, B, C, D"} /> leżą na okręgu o środku
          w punkcie <InlineMath math={"S"} /> i promieniu{" "}
          <InlineMath math={"36"} />. Punkt <InlineMath math={"S"} /> leży na
          odcinku <InlineMath math={"BD"} />. Kąt{" "}
          <InlineMath math={"BDA"} /> ma miarę{" "}
          <InlineMath math={"40^\\circ"} />, a kąt{" "}
          <InlineMath math={"DBC"} /> ma miarę{" "}
          <InlineMath math={"65^\\circ"} />.
        </p>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <MaturaCircleTask21Diagram />
        </div>
        <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
        <p>
          Miara kąta ostrego <InlineMath math={"BSA"} /> jest równa
        </p>
        <div>
          {options.map((option) => (
            <div key={option.label}>
              <InlineMath math={`${option.label}.\\;${option.latex}`} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>

      <p>
        Punkty <InlineMath math={"A, B, P"} /> leżą na okręgu o środku w punkcie{" "}
        <InlineMath math={"O"} />.
      </p>
      <p>
        Kąt wpisany <InlineMath math={"\\angle APB"} /> oparty na łuku{" "}
        <InlineMath math={"AB"} /> ma miarę{" "}
        <InlineMath math={`${alpha_w}^{\\circ}`} />.
      </p>

      <div
        style={{
          marginTop: 8,
          marginBottom: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
        }}
      >
        <CircleAnglesDiagram
          centralDeg={centralDeg}
          showValues={false}
          // na rysunku kąt wpisany ma wartość (z gotowym znakiem °),
          // kąt przy O ma znak zapytania
          inscribedLabel={`${alpha_w}°`}
          centralLabel={"?"}
        />
      </div>

      <p>
        Miara kąta <InlineMath math={"\\angle AOB"} /> jest równa:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options?.[correctIndex];
  if (!correct) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// === explanation (DODANE KROKI) ===

export function renderExplanation({ data }) {
  if (data.originalSharedDiagram) {
    return (
      <div className="space-y-3" style={{ textAlign: "left" }}>
        <p>
          <strong>Krok 1.</strong> Kąt <InlineMath math={"BDA"} /> jest kątem
          wpisanym opartym na łuku <InlineMath math={"BA"} />.
        </p>
        <p>
          <strong>Krok 2.</strong> Kąt środkowy oparty na tym samym łuku jest
          dwa razy większy:
        </p>
        <BlockMath math={"\\angle BSA=2\\cdot40^\\circ=80^\\circ"} />
        <p>Poprawna jest odpowiedź <InlineMath math="D" />.</p>
      </div>
    );
  }

  const { alpha_w, alpha_sr } = data;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Korzystamy z własności: kąt środkowy oparty na tym
        samym łuku jest dwa razy większy od kąta wpisanego.
      </p>

      <BlockMath math={"\\alpha_{\\text{sr}} = 2\\,\\alpha_{w}"} />

      <p>
        <strong>Krok 2.</strong> Podstawiamy miarę kąta wpisanego z zadania:
      </p>

      <BlockMath
        math={`\\alpha_{\\text{sr}} = 2 \\cdot ${alpha_w}^{\\circ} = ${alpha_sr}^{\\circ}`}
      />

      <p>
        <strong>Krok 3.</strong> Szukana miara kąta <InlineMath math={"\\angle AOB"} /> to{" "}
        <InlineMath math={`${alpha_sr}^{\\circ}`} />, więc wybieramy odpowiedź z taką wartością.
      </p>
    </div>
  );
}
